/**
 * MOVEMENT, OFF WHEN SOMEBODY SAYS SO.
 *
 * Anthony, 2026-09-26: "give users a user settings option and they can
 * turn off animation like the spinning coins and cars etc."
 *
 * The coin turns, the big green buttons have a light running round their
 * edge, drawers slide and car pictures grow under the pointer. The browser's
 * own reduce-motion switch already stops all of it, but that switch lives
 * in Windows or iOS settings and nobody standing at a counter goes looking
 * for it. This is the same off switch, inside the app.
 *
 * IT FOLLOWS THE LOGIN. Anthony, same day: "make the animation setting
 * follow the login to every device." The truth is the person's row in
 * my_settings (migration 0181). localStorage is only a copy, kept so
 * index.html can apply it before the first paint instead of spinning for
 * a frame and then stopping. `data-motion` on <html> is what app.css keys
 * off.
 */

import { db } from "../data/client.ts";

const KEY = "motion";

export function motionOn(): boolean {
  return document.documentElement.getAttribute("data-motion") !== "off";
}

/** Just this browser: the attribute and the first-paint copy. */
function applyHere(on: boolean): void {
  if (on) document.documentElement.removeAttribute("data-motion");
  else document.documentElement.setAttribute("data-motion", "off");
  try {
    if (on) localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, "off");
  } catch { /* private window: it still holds for this visit */ }
}

async function save(on: boolean): Promise<void> {
  const sb = await db();
  const { data: auth } = await sb.auth.getUser();
  const id = auth.user?.id;
  if (!id) return;
  // supabase-js returns a refusal rather than throwing it, so throw it here.
  const { error } = await sb.from("my_settings").upsert(
    { profile_id: id, motion_off: !on }, { onConflict: "profile_id" });
  if (error) throw new Error(error.message);
}

/** The switch. Takes effect here at once, then goes to the account. */
export function setMotion(on: boolean): void {
  applyHere(on);
  void save(on).catch(() => { /* this device already has it; next change retries */ });
}

/**
 * Once per sign-in: take what the account says. No row means the default,
 * animations on. The local copy is NOT promoted to the account, because on
 * a shared shop PC it may be the last person's choice, not this one's.
 */
export async function loadMotion(): Promise<void> {
  try {
    const sb = await db();
    const { data: auth } = await sb.auth.getUser();
    const id = auth.user?.id;
    if (!id) return;
    const { data, error } = await sb.from("my_settings")
      .select("motion_off").eq("profile_id", id).maybeSingle();
    if (error) return;
    const row = data as { motion_off: boolean } | null;
    const on = row ? !row.motion_off : true;
    if (on !== motionOn()) {
      applyHere(on);
      const b = document.getElementById("motion2");
      if (b) b.textContent = motionLabel();
      const c = document.getElementById("me-motion") as HTMLInputElement | null;
      if (c) c.checked = on;
    }
  } catch { /* the local copy stands */ }
}

/** What the menu line says: the thing pressing it will do. */
export const motionLabel = (): string => motionOn() ? "Stop animations" : "Allow animations";
